// Superuser management service
// Superusers have full access to all competitions and admin functions
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Hardcoded superuser emails (for initial bootstrap)
const SUPERUSER_EMAILS = [
  'frode@fagerli.info'
];

// Check if an email is a superuser
export const isSuperuserEmail = (email) => {
  if (!email) return false;
  return SUPERUSER_EMAILS.includes(email.toLowerCase());
};

// Get superuser record by Firebase UID
export const getSuperuser = async (uid) => {
  const superuserRef = doc(db, 'superusers', uid);
  const superuserSnap = await getDoc(superuserRef);

  if (!superuserSnap.exists()) {
    return null;
  }

  return {
    id: superuserSnap.id,
    ...superuserSnap.data()
  };
};

// Check if a user is a superuser (by UID)
export const checkIsSuperuser = async (uid) => {
  if (!uid) return false;
  const superuser = await getSuperuser(uid);
  return !!superuser;
};

// Register a superuser (called when a superuser email signs in)
export const registerSuperuser = async (uid, email, displayName) => {
  if (!isSuperuserEmail(email)) {
    throw new Error('Email is not authorized as superuser');
  }

  const superuserRef = doc(db, 'superusers', uid);

  await setDoc(superuserRef, {
    email: email.toLowerCase(),
    displayName: displayName || email.split('@')[0],
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp()
  }, { merge: true });

  return {
    id: uid,
    email,
    displayName
  };
};

// Update superuser last login
export const updateSuperuserLastLogin = async (uid) => {
  const superuserRef = doc(db, 'superusers', uid);
  await setDoc(superuserRef, {
    lastLoginAt: serverTimestamp()
  }, { merge: true });
};

// Get all superusers
export const getAllSuperusers = async () => {
  const superusersRef = collection(db, 'superusers');
  const querySnapshot = await getDocs(superusersRef);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Remove superuser access (only another superuser can do this)
export const removeSuperuser = async (uid) => {
  const superuserRef = doc(db, 'superusers', uid);
  await deleteDoc(superuserRef);
};

// Get all users from Firebase Auth (requires admin tools - for now, query by known data)
// This queries all playerTokens to find unique authUids
export const getAllAuthUsers = async () => {
  const tokensRef = collection(db, 'playerTokens');
  const querySnapshot = await getDocs(tokensRef);

  // Group by authUid to get unique users
  const usersMap = new Map();

  querySnapshot.docs.forEach(doc => {
    const data = doc.data();
    if (data.authUid && !usersMap.has(data.authUid)) {
      usersMap.set(data.authUid, {
        authUid: data.authUid,
        playerId: data.playerId,
        competitionId: data.competitionId,
        deviceLabel: data.deviceLabel,
        lastSeenAt: data.lastSeenAt,
        createdAt: data.createdAt
      });
    }
  });

  return Array.from(usersMap.values());
};

// Get all league members (returns unique odplayerIds with their display names)
export const getAllLeagueMembers = async () => {
  const leaguesRef = collection(db, 'leagues');
  const querySnapshot = await getDocs(leaguesRef);

  const membersMap = new Map();

  querySnapshot.docs.forEach(doc => {
    const data = doc.data();
    if (data.members && Array.isArray(data.members)) {
      data.members.forEach(member => {
        const id = member.odplayerId || member.playerId;
        if (id && !membersMap.has(id)) {
          membersMap.set(id, {
            odplayerId: id,
            displayName: member.displayName,
            role: member.role,
            leagueId: doc.id,
            leagueName: data.name
          });
        }
      });
    }
  });

  return Array.from(membersMap.values());
};

// Get comprehensive user data for cleanup/migration
export const getUserDataForMigration = async () => {
  // Get all players
  const playersRef = collection(db, 'players');
  const playersSnap = await getDocs(playersRef);
  const players = playersSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all player tokens
  const tokensRef = collection(db, 'playerTokens');
  const tokensSnap = await getDocs(tokensRef);
  const tokens = tokensSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all leagues
  const leaguesRef = collection(db, 'leagues');
  const leaguesSnap = await getDocs(leaguesRef);
  const leagues = leaguesSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all competitions
  const competitionsRef = collection(db, 'competitions');
  const competitionsSnap = await getDocs(competitionsRef);
  const competitions = competitionsSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all results
  const resultsRef = collection(db, 'results');
  const resultsSnap = await getDocs(resultsRef);
  const results = resultsSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all game votes
  const gameVotesRef = collection(db, 'gameVotes');
  const gameVotesSnap = await getDocs(gameVotesRef);
  const gameVotes = gameVotesSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all game ratings
  const gameRatingsRef = collection(db, 'gameRatings');
  const gameRatingsSnap = await getDocs(gameRatingsRef);
  const gameRatings = gameRatingsSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all achievements
  const achievementsRef = collection(db, 'achievements');
  const achievementsSnap = await getDocs(achievementsRef);
  const achievements = achievementsSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get all games (for reference)
  const gamesRef = collection(db, 'games');
  const gamesSnap = await getDocs(gamesRef);
  const games = gamesSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Analyze user data
  const analysis = {
    totalPlayers: players.length,
    playersWithOdplayerId: players.filter(p => p.odplayerId).length,
    playersWithoutOdplayerId: players.filter(p => !p.odplayerId).length,
    totalTokens: tokens.length,
    uniqueAuthUids: new Set(tokens.map(t => t.authUid)).size,
    totalLeagues: leagues.length,
    totalCompetitions: competitions.length,
    totalResults: results.length,
    totalGameVotes: gameVotes.length,
    totalGameRatings: gameRatings.length,
    totalAchievements: achievements.length,
    totalGames: games.length,

    // Group players by competition
    playersByCompetition: {},

    // Find orphaned players (no tokens pointing to them)
    orphanedPlayers: [],

    // Find duplicate display names across competitions
    duplicateNames: {}
  };

  // Group players by competition
  players.forEach(player => {
    if (!analysis.playersByCompetition[player.competitionId]) {
      analysis.playersByCompetition[player.competitionId] = [];
    }
    analysis.playersByCompetition[player.competitionId].push(player);
  });

  // Find orphaned players
  const playerIdsWithTokens = new Set(tokens.map(t => t.playerId));
  analysis.orphanedPlayers = players.filter(p => !playerIdsWithTokens.has(p.id) && !p.odplayerId);

  // Find duplicate names
  const nameCount = {};
  players.forEach(player => {
    const key = player.displayName?.toLowerCase().trim();
    if (key) {
      if (!nameCount[key]) {
        nameCount[key] = [];
      }
      nameCount[key].push({
        playerId: player.id,
        competitionId: player.competitionId,
        odplayerId: player.odplayerId
      });
    }
  });
  analysis.duplicateNames = Object.fromEntries(
    Object.entries(nameCount).filter(([_, v]) => v.length > 1)
  );

  // Get superusers for auth info
  const superusersRef = collection(db, 'superusers');
  const superusersSnap = await getDocs(superusersRef);
  const superusers = superusersSnap.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Get auth emails mapping (manually stored since we can't query Firebase Auth directly)
  const authEmailsRef = collection(db, 'authEmails');
  const authEmailsSnap = await getDocs(authEmailsRef);
  const authEmailsMap = new Map();
  authEmailsSnap.docs.forEach(doc => {
    const data = doc.data();
    authEmailsMap.set(doc.id, data.email);
  });

  // Build auth users list from various sources
  const authUsersMap = new Map();

  // Add from superusers (these have email)
  superusers.forEach(su => {
    authUsersMap.set(su.id, {
      authUid: su.id,
      email: su.email,
      displayName: su.displayName,
      provider: su.email?.includes('@gmail.com') ? 'google' : 'email',
      isSuperuser: true,
      lastLoginAt: su.lastLoginAt,
      sources: ['superusers']
    });
  });

  // Add from player tokens
  tokens.forEach(token => {
    if (token.authUid) {
      if (authUsersMap.has(token.authUid)) {
        const existing = authUsersMap.get(token.authUid);
        if (!existing.sources.includes('tokens')) {
          existing.sources.push('tokens');
        }
      } else {
        authUsersMap.set(token.authUid, {
          authUid: token.authUid,
          email: null,
          displayName: null,
          provider: 'anonymous',
          isSuperuser: false,
          lastSeenAt: token.lastSeenAt,
          sources: ['tokens']
        });
      }
    }
  });

  // Add from league members
  leagues.forEach(league => {
    if (league.members) {
      league.members.forEach(member => {
        const memberId = member.odplayerId || member.playerId;
        if (memberId) {
          if (authUsersMap.has(memberId)) {
            const existing = authUsersMap.get(memberId);
            if (!existing.sources.includes('leagues')) {
              existing.sources.push('leagues');
            }
            // Update display name if we have one
            if (member.displayName && !existing.displayName) {
              existing.displayName = member.displayName;
            }
            // If they're in a league, they're likely not anonymous
            if (existing.provider === 'anonymous') {
              existing.provider = 'unknown';
            }
          } else {
            authUsersMap.set(memberId, {
              authUid: memberId,
              email: null,
              displayName: member.displayName,
              provider: 'unknown',
              isSuperuser: false,
              sources: ['leagues']
            });
          }
        }
      });
    }
  });

  // Add from players with odplayerId
  players.forEach(player => {
    if (player.odplayerId) {
      if (authUsersMap.has(player.odplayerId)) {
        const existing = authUsersMap.get(player.odplayerId);
        if (!existing.sources.includes('players')) {
          existing.sources.push('players');
        }
        if (player.displayName && !existing.displayName) {
          existing.displayName = player.displayName;
        }
      } else {
        authUsersMap.set(player.odplayerId, {
          authUid: player.odplayerId,
          email: null,
          displayName: player.displayName,
          provider: 'unknown',
          isSuperuser: false,
          sources: ['players']
        });
      }
    }
  });

  // Apply email mappings from authEmails collection as fallback
  authUsersMap.forEach((user, authUid) => {
    if (!user.email && authEmailsMap.has(authUid)) {
      user.email = authEmailsMap.get(authUid);
      user.provider = user.email?.includes('@gmail.com') ? 'google' : 'email';
      if (!user.sources.includes('authEmails')) {
        user.sources.push('authEmails');
      }
    }
  });

  const authUsers = Array.from(authUsersMap.values());

  // Count by provider
  analysis.authUsersByProvider = {
    google: authUsers.filter(u => u.provider === 'google').length,
    email: authUsers.filter(u => u.provider === 'email').length,
    anonymous: authUsers.filter(u => u.provider === 'anonymous').length,
    unknown: authUsers.filter(u => u.provider === 'unknown').length,
    total: authUsers.length
  };

  return {
    players,
    tokens,
    leagues,
    competitions,
    results,
    gameVotes,
    gameRatings,
    achievements,
    games,
    authUsers,
    superusers,
    analysis
  };
};

// Save auth email mapping (since we can't query Firebase Auth directly from client)
export const saveAuthEmail = async (authUid, email) => {
  const authEmailRef = doc(db, 'authEmails', authUid);
  await setDoc(authEmailRef, {
    email,
    updatedAt: serverTimestamp()
  });
};

// Merge two Player RECORDS (by Player ID) - transfers results from source to target player
// Use this when the same person has two different player entries in the same competition
export const mergePlayerRecords = async (sourcePlayerId, targetPlayerId) => {
  const { writeBatch, updateDoc } = await import('firebase/firestore');

  // Get both players to verify they exist
  const sourcePlayerRef = doc(db, 'players', sourcePlayerId);
  const targetPlayerRef = doc(db, 'players', targetPlayerId);

  const [sourceSnap, targetSnap] = await Promise.all([
    getDoc(sourcePlayerRef),
    getDoc(targetPlayerRef)
  ]);

  if (!sourceSnap.exists()) {
    throw new Error(`Source player ${sourcePlayerId} not found`);
  }
  if (!targetSnap.exists()) {
    throw new Error(`Target player ${targetPlayerId} not found`);
  }

  const sourcePlayer = { id: sourceSnap.id, ...sourceSnap.data() };
  const targetPlayer = { id: targetSnap.id, ...targetSnap.data() };

  // Note: Players can be in different competitions - this allows merging a person's data across competitions

  // Find all results for the source player
  const resultsRef = collection(db, 'results');
  const resultsQuery = query(resultsRef, where('playerId', '==', sourcePlayerId));
  const resultsSnap = await getDocs(resultsQuery);

  // Find all game votes for the source player
  const votesRef = collection(db, 'gameVotes');
  const votesQuery = query(votesRef, where('playerId', '==', sourcePlayerId));
  const votesSnap = await getDocs(votesQuery);

  // Find all game ratings for the source player
  const ratingsRef = collection(db, 'gameRatings');
  const ratingsQuery = query(ratingsRef, where('playerId', '==', sourcePlayerId));
  const ratingsSnap = await getDocs(ratingsQuery);

  // Find all achievements for the source player
  const achievementsRef = collection(db, 'achievements');
  const achievementsQuery = query(achievementsRef, where('playerId', '==', sourcePlayerId));
  const achievementsSnap = await getDocs(achievementsQuery);

  // Find all player tokens for the source player
  const tokensRef = collection(db, 'playerTokens');
  const tokensQuery = query(tokensRef, where('playerId', '==', sourcePlayerId));
  const tokensSnap = await getDocs(tokensQuery);

  // Update all results to point to target player
  const resultUpdatePromises = resultsSnap.docs.map(docSnap => {
    const resultRef = doc(db, 'results', docSnap.id);
    return setDoc(resultRef, {
      playerId: targetPlayerId,
      mergedFrom: sourcePlayerId,
      mergedAt: serverTimestamp()
    }, { merge: true });
  });

  // Update all votes to point to target player
  const voteUpdatePromises = votesSnap.docs.map(docSnap => {
    const voteRef = doc(db, 'gameVotes', docSnap.id);
    return setDoc(voteRef, {
      playerId: targetPlayerId,
      mergedFrom: sourcePlayerId,
      mergedAt: serverTimestamp()
    }, { merge: true });
  });

  // Update all ratings to point to target player
  const ratingUpdatePromises = ratingsSnap.docs.map(docSnap => {
    const ratingRef = doc(db, 'gameRatings', docSnap.id);
    return setDoc(ratingRef, {
      playerId: targetPlayerId,
      mergedFrom: sourcePlayerId,
      mergedAt: serverTimestamp()
    }, { merge: true });
  });

  // Update all achievements to point to target player
  const achievementUpdatePromises = achievementsSnap.docs.map(docSnap => {
    const achievementRef = doc(db, 'achievements', docSnap.id);
    return setDoc(achievementRef, {
      playerId: targetPlayerId,
      mergedFrom: sourcePlayerId,
      mergedAt: serverTimestamp()
    }, { merge: true });
  });

  // Update all tokens to point to target player
  const tokenUpdatePromises = tokensSnap.docs.map(docSnap => {
    const tokenRef = doc(db, 'playerTokens', docSnap.id);
    return setDoc(tokenRef, {
      playerId: targetPlayerId,
      mergedFrom: sourcePlayerId,
      mergedAt: serverTimestamp()
    }, { merge: true });
  });

  await Promise.all([
    ...resultUpdatePromises,
    ...voteUpdatePromises,
    ...ratingUpdatePromises,
    ...achievementUpdatePromises,
    ...tokenUpdatePromises
  ]);

  // Mark the source player as merged (don't delete - keep for audit trail)
  await setDoc(sourcePlayerRef, {
    status: 'merged',
    mergedInto: targetPlayerId,
    mergedAt: serverTimestamp()
  }, { merge: true });

  // If source player had odplayerId and target doesn't, transfer it
  if (sourcePlayer.odplayerId && !targetPlayer.odplayerId) {
    await setDoc(targetPlayerRef, {
      odplayerId: sourcePlayer.odplayerId,
      updatedAt: serverTimestamp()
    }, { merge: true });
  }

  return {
    resultsTransferred: resultsSnap.docs.length,
    votesTransferred: votesSnap.docs.length,
    ratingsTransferred: ratingsSnap.docs.length,
    achievementsTransferred: achievementsSnap.docs.length,
    tokensTransferred: tokensSnap.docs.length,
    sourcePlayer: sourcePlayer.displayName,
    targetPlayer: targetPlayer.displayName,
    competition: sourcePlayer.competitionId
  };
};

// Unmerge a player - restore a merged player back to active status
export const unmergePlayer = async (playerId) => {
  const playerRef = doc(db, 'players', playerId);
  const playerSnap = await getDoc(playerRef);

  if (!playerSnap.exists()) {
    throw new Error(`Player ${playerId} not found`);
  }

  const player = playerSnap.data();

  if (player.status !== 'merged') {
    throw new Error(`Player ${playerId} is not merged (status: ${player.status})`);
  }

  // Restore to active status
  await setDoc(playerRef, {
    status: 'active',
    unmergedAt: serverTimestamp()
  }, { merge: true });

  return {
    playerId,
    displayName: player.displayName,
    competitionId: player.competitionId,
    previouslyMergedInto: player.mergedInto
  };
};

// Merge two player accounts (used when same person has multiple authUids)
export const mergePlayerAccounts = async (sourceAuthUid, targetAuthUid) => {
  // Find all tokens with source authUid and update to target
  const tokensRef = collection(db, 'playerTokens');
  const q = query(tokensRef, where('authUid', '==', sourceAuthUid));
  const tokensSnap = await getDocs(q);

  const updatePromises = tokensSnap.docs.map(doc => {
    const tokenRef = doc(db, 'playerTokens', doc.id);
    return setDoc(tokenRef, {
      authUid: targetAuthUid,
      mergedFrom: sourceAuthUid,
      mergedAt: serverTimestamp()
    }, { merge: true });
  });

  await Promise.all(updatePromises);

  // Find all players with source odplayerId and update to target
  const playersRef = collection(db, 'players');
  const playersQuery = query(playersRef, where('odplayerId', '==', sourceAuthUid));
  const playersSnap = await getDocs(playersQuery);

  const playerUpdatePromises = playersSnap.docs.map(docSnap => {
    const playerRef = doc(db, 'players', docSnap.id);
    return setDoc(playerRef, {
      odplayerId: targetAuthUid,
      mergedFrom: sourceAuthUid,
      mergedAt: serverTimestamp()
    }, { merge: true });
  });

  await Promise.all(playerUpdatePromises);

  // Update league memberships
  const leaguesRef = collection(db, 'leagues');
  const leaguesSnap = await getDocs(leaguesRef);

  for (const leagueDoc of leaguesSnap.docs) {
    const league = leagueDoc.data();
    let updated = false;

    // Update memberIds array
    if (league.memberIds?.includes(sourceAuthUid)) {
      const newMemberIds = league.memberIds.filter(id => id !== sourceAuthUid);
      if (!newMemberIds.includes(targetAuthUid)) {
        newMemberIds.push(targetAuthUid);
      }
      league.memberIds = newMemberIds;
      updated = true;
    }

    // Update members array
    if (league.members) {
      league.members = league.members.map(member => {
        if (member.odplayerId === sourceAuthUid || member.playerId === sourceAuthUid) {
          updated = true;
          return {
            ...member,
            odplayerId: targetAuthUid,
            playerId: targetAuthUid
          };
        }
        return member;
      });
    }

    // Update adminId if needed
    if (league.adminId === sourceAuthUid) {
      league.adminId = targetAuthUid;
      updated = true;
    }

    if (updated) {
      const leagueRef = doc(db, 'leagues', leagueDoc.id);
      await setDoc(leagueRef, {
        ...league,
        updatedAt: serverTimestamp()
      });
    }
  }

  return {
    tokensUpdated: tokensSnap.docs.length,
    playersUpdated: playersSnap.docs.length
  };
};

// Fix league membership - update memberIds to use Auth UIDs instead of Player IDs
// This is needed because leagues should query by Auth UID but may have been set up with Player IDs
export const fixLeagueMembership = async (leagueId) => {
  // Get the league
  const leagueRef = doc(db, 'leagues', leagueId);
  const leagueSnap = await getDoc(leagueRef);

  if (!leagueSnap.exists()) {
    throw new Error(`League ${leagueId} not found`);
  }

  const league = leagueSnap.data();
  const originalMemberIds = [...(league.memberIds || [])];
  const updatedMemberIds = [];
  const updatedMembers = [];
  const fixes = [];

  console.log('=== Fix League Membership ===');
  console.log('League:', league.name);
  console.log('Original memberIds:', originalMemberIds);

  // Get all players to build a map of Player ID -> Auth UID
  const playersRef = collection(db, 'players');
  const playersSnap = await getDocs(playersRef);
  const playerIdToAuthUid = new Map();
  const authUidToPlayer = new Map();

  playersSnap.docs.forEach(docSnap => {
    const player = docSnap.data();
    // Map Player doc ID to Auth UID (if player has odplayerId)
    if (player.odplayerId) {
      playerIdToAuthUid.set(docSnap.id, player.odplayerId);
      // Also keep track of which Auth UIDs we know about
      if (!authUidToPlayer.has(player.odplayerId)) {
        authUidToPlayer.set(player.odplayerId, {
          displayName: player.displayName,
          playerId: docSnap.id
        });
      }
    }
  });

  console.log('Player ID -> Auth UID mappings:', playerIdToAuthUid.size);

  // Process each member
  for (const member of (league.members || [])) {
    const currentId = member.playerId || member.odplayerId;
    let newId = currentId;
    let fixed = false;

    // Check if this ID is a Player ID (UUID format) that has an Auth UID
    if (playerIdToAuthUid.has(currentId)) {
      newId = playerIdToAuthUid.get(currentId);
      fixed = true;
      fixes.push({
        displayName: member.displayName,
        oldId: currentId,
        newId: newId,
        type: 'player_to_auth'
      });
      console.log(`  ${member.displayName}: Player ID ${currentId} -> Auth UID ${newId}`);
    }
    // Check if the ID in memberIds looks like a Player ID and needs conversion
    else if (currentId && currentId.includes('-') && currentId.length === 36) {
      // This looks like a UUID (Player ID) - try to find the auth UID
      const authUid = playerIdToAuthUid.get(currentId);
      if (authUid) {
        newId = authUid;
        fixed = true;
        fixes.push({
          displayName: member.displayName,
          oldId: currentId,
          newId: newId,
          type: 'uuid_to_auth'
        });
        console.log(`  ${member.displayName}: UUID ${currentId} -> Auth UID ${newId}`);
      } else {
        console.log(`  ${member.displayName}: UUID ${currentId} - no Auth UID found (keeping as-is)`);
      }
    } else {
      console.log(`  ${member.displayName}: ${currentId} - already Auth UID or unknown format`);
    }

    // Add to updated arrays (avoid duplicates)
    if (!updatedMemberIds.includes(newId)) {
      updatedMemberIds.push(newId);
    }

    updatedMembers.push({
      ...member,
      playerId: newId,
      odplayerId: newId
    });
  }

  // Also check memberIds array for any IDs not in members
  for (const memberId of originalMemberIds) {
    if (!updatedMemberIds.includes(memberId)) {
      const authUid = playerIdToAuthUid.get(memberId);
      if (authUid && !updatedMemberIds.includes(authUid)) {
        updatedMemberIds.push(authUid);
        fixes.push({
          displayName: 'Unknown',
          oldId: memberId,
          newId: authUid,
          type: 'memberIds_only'
        });
        console.log(`  memberIds entry: ${memberId} -> ${authUid}`);
      } else if (!authUid) {
        // Keep the original if no mapping found
        if (!updatedMemberIds.includes(memberId)) {
          updatedMemberIds.push(memberId);
        }
      }
    }
  }

  // Also fix adminId if needed
  let adminFixed = false;
  let newAdminId = league.adminId;
  if (league.adminId && playerIdToAuthUid.has(league.adminId)) {
    newAdminId = playerIdToAuthUid.get(league.adminId);
    adminFixed = true;
    fixes.push({
      displayName: 'Admin',
      oldId: league.adminId,
      newId: newAdminId,
      type: 'admin'
    });
    console.log(`  Admin: ${league.adminId} -> ${newAdminId}`);
  }

  console.log('Updated memberIds:', updatedMemberIds);
  console.log('Total fixes:', fixes.length);

  // Update the league
  await setDoc(leagueRef, {
    members: updatedMembers,
    memberIds: updatedMemberIds,
    adminId: newAdminId,
    updatedAt: serverTimestamp()
  }, { merge: true });

  return {
    leagueName: league.name,
    originalMemberIds,
    updatedMemberIds,
    fixes,
    adminFixed
  };
};

// Replace an Auth UID in a league's memberIds (for fixing typos/truncated UIDs)
export const replaceLeagueMemberId = async (leagueId, oldId, newId) => {
  const leagueRef = doc(db, 'leagues', leagueId);
  const leagueSnap = await getDoc(leagueRef);

  if (!leagueSnap.exists()) {
    throw new Error(`League ${leagueId} not found`);
  }

  const league = leagueSnap.data();

  // Check if oldId exists in memberIds
  if (!league.memberIds?.includes(oldId)) {
    throw new Error(`Old ID ${oldId} not found in league memberIds`);
  }

  // Update memberIds array
  const updatedMemberIds = league.memberIds.map(id => id === oldId ? newId : id);

  // Update members array
  const updatedMembers = (league.members || []).map(member => {
    if (member.playerId === oldId || member.odplayerId === oldId) {
      return {
        ...member,
        playerId: newId,
        odplayerId: newId
      };
    }
    return member;
  });

  // Update adminId if needed
  const updatedAdminId = league.adminId === oldId ? newId : league.adminId;

  await setDoc(leagueRef, {
    memberIds: updatedMemberIds,
    members: updatedMembers,
    adminId: updatedAdminId,
    updatedAt: serverTimestamp()
  }, { merge: true });

  return {
    leagueName: league.name,
    oldId,
    newId,
    adminUpdated: league.adminId === oldId
  };
};
